import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import ApiService from "../../../../service/ApiService";
import { apiPaths } from "../../../../service/apiPaths";
import { useNavigate } from "react-router-dom";

export interface ICouponCategory {
  name: string;
  _id: string;
}

export interface IStudent {
  _id: string;
  email: string;
  mobileNo: string;
  name: string;
}

export interface IAddCoupon {
  type: string;
  discountType: "PERCENTAGE" | "FIXED";
  discountValue: string;
  maxDiscountAmount: string | null;
  isActive: boolean;
  validFrom: Date | string;
  validUntil: Date | string;
  usageLimit: string;
  usedCount: number;
  assignmentType?: "everyone" | "specific";
  assignedUsers?: string[];
  isForNewUsersOnly:boolean
}

const useAddCoupon = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedStudents, setSelectedStudents] = useState<IStudent[]>([]);
  const [assignmentType, setAssignmentType] = useState<"everyone" | "specific">("everyone");
  const [couponCategories, setCouponCategories] = useState<ICouponCategory[]>([]);
  const [formData, setFormData] = useState<IAddCoupon>({
    type: "",
    discountType: "PERCENTAGE",
    isForNewUsersOnly:false ,
    discountValue: "0",
    maxDiscountAmount: null,
    isActive: true,
    validFrom: new Date().toISOString().split("T")[0],
    validUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
    usageLimit: "-1",
    usedCount: 0,
    assignmentType: "everyone",
    assignedUsers: [],
  });

  const navigate = useNavigate();

  useEffect(() => {
    // Fetch coupon categories
    const fetchCouponCategories = async () => {
      setLoading(true);
      try {
        const response = await ApiService({
          method: "GET",
          endpoint: `${apiPaths.coupons}/categories`,
        });

        setCouponCategories(response.categories);
      } catch (error) {
        console.error("Error fetching coupon categories:", error);
        toast.error("Failed to load coupon categories");
      } finally {
        setLoading(false);
      }
    };

    fetchCouponCategories();
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { id, value, type } = e.target;

    if (type === "checkbox") {
      setFormData((prev) => ({
        ...prev,
        [id]: (e.target as HTMLInputElement).checked,
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleAssignmentTypeChange = (type: "everyone" | "specific") => {
    setAssignmentType(type);
    setFormData((prev) => ({
      ...prev,
      assignmentType: type,
    }));
  };

  const handleStudentSelect = (student: IStudent) => {
    // Check if student is already selected
    if (selectedStudents.some((s) => s._id === student._id)) {
      toast.error("Student already selected");
      return;
    }

    // Add student to selected list
    setSelectedStudents((prev) => [...prev, student]);
    
    setFormData((prev) => ({
      ...prev,
      assignedUsers: [...(prev.assignedUsers || []), student._id],
    }));

    toast.success(`${student.name || student.email} added to coupon recipients`);
  };

  const handleRemoveStudent = (id: string) => {
    // Remove student from selected list
    setSelectedStudents((prev) => prev.filter((student) => student._id !== id));
    
    // Update form data
    setFormData((prev) => ({
      ...prev,
      assignedUsers: prev.assignedUsers?.filter((studentId) => studentId !== id) || [],
    }));
  };

  const searchStudents = async (searchTerm: string) => {
    if (searchTerm.trim().length < 3) {
      toast.error("Please enter at least 3 characters to search");
      return [];
    }

    setLoading(true);
    try {
      const data = await ApiService({
        method: "GET",
        endpoint: `${apiPaths.searchUsers}/${searchTerm}`,
      });

      if (!data || !data.students || data.students.length === 0) {
        toast.error("No students found matching the search term");
        return [];
      }

      return data.students;
    } catch (err) {
      console.error("Error searching students:", err);
      toast.error("Failed to search students");
      return [];
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (data: IAddCoupon) => {
    setLoading(true);
    try {
      // Prepare data for submission
      const submissionData = {
        ...data,
        assignmentType,
        // Only include studentIds if assignment type is specific
        ...(assignmentType === "specific" && {
          assignedUsers: selectedStudents.map((student) => student._id),
        }),
      };

      // Validate student selection if specific assignment is chosen
      if (assignmentType === "specific" && (!selectedStudents.length || !submissionData.assignedUsers?.length)) {
        toast.error("Please select at least one student");
        setLoading(false);
        return;
      }
      
      if(new Date(data?.validFrom) > new Date(data?.validUntil)) {
        toast.error("Valid From date cannot be later than Valid Until date");
        setLoading(false);
        return;
      }

      console.log(submissionData)

      const response = await ApiService({
        method: "POST",
        endpoint: apiPaths.coupons,
        data: submissionData,
      });

      toast.success("Coupon added successfully!");
      
      // Reset form after successful submission
      handleCancel();
    } catch (error) {
      console.error("Error adding coupon:", error);
      toast.error("Error adding coupon");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset form data
    setFormData({
      type: "",
      discountType: "PERCENTAGE",
      discountValue: "0",
      maxDiscountAmount: null,
      isActive: true,
      validFrom: new Date().toISOString().split("T")[0],
      validUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
      usageLimit: "-1",
      usedCount: 0,
      assignmentType: "everyone",
      assignedUsers: [],
      isForNewUsersOnly:false
    });
    
    // Clear selected students
    setSelectedStudents([]);
    // Reset assignment type
    setAssignmentType("everyone");
    
    // Navigate back to coupon list
    navigate("/coupon");
  };

  return {
    formData,
    loading,
    couponCategories,
    handleChange,
    handleSubmit,
    handleCancel,
    handleRemoveStudent,
    handleStudentSelect,
    selectedStudents,
    searchStudents,
    assignmentType,
    handleAssignmentTypeChange,
  };
};

export default useAddCoupon;